import { actionIsCommitted, actionIsCompleted, shiftDate, weekStart } from "~vue/pages/developmentJourneys/progressInsights/overviewMetrics";
import type { WeeklyAction } from "~vue/pages/developmentJourneys/weeklyActions";
import { formatDateRange } from "~vue/utils/formatDate";

export interface ActivityWeek {
    start: string;
    end: string;
    committed: number;
    completed: number;
    current: boolean;
}

export interface CalendarWeek extends ActivityWeek {
    upcoming: boolean;
}

export interface ProgressQuarter {
    start: string;
    end: string;
    label: string;
    current: boolean;
    weeks: CalendarWeek[];
}

const quarterStart = (date: string, offset = 0): string => {
    const value = new Date(`${date}T00:00:00Z`);
    return new Date(Date.UTC(value.getUTCFullYear(), Math.floor(value.getUTCMonth() / 3) * 3 + offset * 3, 1)).toISOString().slice(0, 10);
};

export const progressCalendar = (actions: WeeklyAction[], asOfDate: string, historyDates: string[] = [], timezone = "UTC"): ProgressQuarter[] => {
    const currentWeekQuarter = quarterStart(weekStart(asOfDate));
    const nextQuarter = quarterStart(currentWeekQuarter, 1);
    const lastQuarter = actions.some(
        (action) =>
            actionIsCommitted(action, asOfDate, timezone) && weekStart(action.start_date) >= nextQuarter && weekStart(action.start_date) < quarterStart(currentWeekQuarter, 2),
    )
        ? nextQuarter
        : currentWeekQuarter;
    const firstDate =
        [...historyDates, ...actions.filter((action) => actionIsCommitted(action, asOfDate, timezone)).map((action) => action.start_date)]
            .filter((date) => date <= asOfDate)
            .sort((a, b) => a.localeCompare(b))[0] ?? asOfDate;
    const firstQuarter = [quarterStart(weekStart(firstDate)), quarterStart(lastQuarter, -3)].sort((a, b) => a.localeCompare(b))[1];
    const quarters: ProgressQuarter[] = [];
    for (let start = firstQuarter; start <= lastQuarter; start = quarterStart(start, 1)) {
        const end = shiftDate(quarterStart(start, 1), -1);
        const weeks: CalendarWeek[] = [];
        const quarterOpeningWeek = weekStart(start);
        for (let week = quarterOpeningWeek < start ? shiftDate(quarterOpeningWeek, 7) : quarterOpeningWeek; week <= end; week = shiftDate(week, 7)) {
            const weekEnd = shiftDate(week, 6);
            const commitments = actions.filter((action) => action.start_date >= week && action.start_date <= weekEnd && actionIsCommitted(action, asOfDate, timezone));
            weeks.push({
                start: week,
                end: weekEnd,
                current: week <= asOfDate && weekEnd >= asOfDate,
                upcoming: week > asOfDate,
                committed: commitments.length,
                completed: commitments.filter((action) => actionIsCompleted(action, asOfDate, timezone)).length,
            });
        }
        quarters.push({
            start,
            end,
            label: formatDateRange(start, end, "month-year"),
            current: weeks.some((week) => week.current),
            weeks,
        });
    }
    return quarters;
};
